import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Flex } from './shared/Flex';
import CloseButton from './shared/CloseButton';
import { useSMS, useSMSConfig } from '../hooks';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: white;
  border-radius: 12px 12px 0 0;
  z-index: 10;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  text-align: center;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 8px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const MetricCard = styled.div<{ isEnabled: boolean }>`
  background: ${({ isEnabled }) => isEnabled ? '#f8fafc' : '#f9fafb'};
  border: 1px solid ${({ isEnabled }) => isEnabled ? '#e2e8f0' : '#e5e7eb'};
  border-radius: 8px;
  padding: 16px;
  opacity: ${({ isEnabled }) => isEnabled ? 1 : 0.6};
`;

const MetricLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.div<{ isEnabled: boolean }>`
  font-size: 18px;
  font-weight: 700;
  color: ${({ isEnabled }) => isEnabled ? '#111827' : '#9ca3af'};
`;

const MetricUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
  margin-left: 4px;
`;

const ConfidenceLevel = styled.div<{ level?: string }>`
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
  color: ${({ level }) => {
    switch (level) {
      case 'HIGH': return '#059669';
      case 'MEDIUM': return '#d97706';
      case 'LOW': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const RiskLevel = styled.div<{ risk?: number }>`
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
  color: ${({ risk }) => {
    if (risk === undefined || risk === 0) return '#6b7280';
    if (risk === 1) return '#059669'; // Low risk - green
    if (risk === 2) return '#d97706'; // Medium risk - orange
    return '#dc2626'; // High risk - red
  }};
`;

const Footer = styled.div`
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;

const ActionButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const SMSButton = styled(ActionButton)`
  background: #10b981;
  
  &:hover:not(:disabled) {
    background: #059669;
  }
`;

const SMSStatus = styled.div<{ success?: boolean }>`
  font-size: 14px;
  color: ${props => props.success ? '#059669' : '#dc2626'};
  text-align: center;
  margin-top: 8px;
`;

interface VitalSign {
  value?: any;
  isEnabled: boolean;
  confidenceLevel?: number;
}

interface ResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  vitalSigns: {
    // Primary vital signs
    pulseRate: VitalSign;
    respirationRate: VitalSign;
    oxygenSaturation: VitalSign;
    bloodPressure: VitalSign;
    
    // HRV metrics
    sdnn: VitalSign;
    rmssd: VitalSign;
    sd1: VitalSign;
    sd2: VitalSign;
    meanRri: VitalSign;
    rri: VitalSign;
    prq: VitalSign;
    lfhf: VitalSign;
    
    // Stress and wellness
    stressLevel: VitalSign;
    stressIndex: VitalSign;
    normalizedStressIndex: VitalSign;
    wellnessLevel: VitalSign;
    wellnessIndex: VitalSign;
    
    // Autonomic nervous system
    pnsIndex: VitalSign;
    pnsZone: VitalSign;
    snsIndex: VitalSign;
    snsZone: VitalSign;
    
    // Blood and health metrics
    hemoglobin: VitalSign;
    hemoglobinA1c: VitalSign;
    heartAge: VitalSign;
    
    // Risk assessments
    highBloodPressureRisk: VitalSign;
    highHemoglobinA1CRisk: VitalSign;
    highFastingGlucoseRisk: VitalSign;
    highTotalCholesterolRisk: VitalSign;
    lowHemoglobinRisk: VitalSign;
    ascvdRisk: VitalSign;
  };
}

const formatValue = (metric: VitalSign, unit: string = '') => {
  if (!metric.isEnabled) return 'N/A';
  if (metric.value === null || metric.value === undefined) return '--';
  
  // Handle blood pressure specially
  if (typeof metric.value === 'object' && metric.value.systolic && metric.value.diastolic) {
    return `${metric.value.systolic}/${metric.value.diastolic}`;
  }
  
  // Handle arrays (like RRI data)
  if (Array.isArray(metric.value)) {
    return `${metric.value.length} samples`;
  }
  
  return `${metric.value}${unit}`;
};

const getConfidenceLevelText = (level?: number) => {
  switch (level) {
    case 3: return 'HIGH';
    case 2: return 'MEDIUM';
    case 1: return 'LOW';
    default: return '';
  }
};

const getRiskLevelText = (risk?: number) => {
  switch (risk) {
    case 1: return 'LOW RISK';
    case 2: return 'MEDIUM RISK';
    case 3: return 'HIGH RISK';
    default: return '';
  }
};

const ResultsModal: React.FC<ResultsModalProps> = ({ isOpen, onClose, vitalSigns }) => {
  const { sendHealthResults, isSending, lastResult } = useSMS();
  const { smsConfig } = useSMSConfig();
  
  // Auto-send SMS when modal opens if SMS is enabled
  useEffect(() => {
    if (isOpen && smsConfig.enabled && smsConfig.phoneNumbers.length > 0 && vitalSigns) {
      console.log('Auto-sending SMS for health results...');
      sendHealthResults(vitalSigns, smsConfig);
    }
  }, [isOpen, smsConfig, vitalSigns, sendHealthResults]);

  const handleManualSMS = useCallback(async () => {
    if (vitalSigns) {
      await sendHealthResults(vitalSigns, smsConfig);
    }
  }, [vitalSigns, smsConfig, sendHealthResults]);

  if (!isOpen) return null;

  const primaryMetrics = [
    { key: 'pulseRate', label: 'Pulse Rate', unit: ' bpm', metric: vitalSigns.pulseRate },
    { key: 'respirationRate', label: 'Respiration Rate', unit: ' rpm', metric: vitalSigns.respirationRate },
    { key: 'oxygenSaturation', label: 'Oxygen Saturation', unit: '%', metric: vitalSigns.oxygenSaturation },
    { key: 'bloodPressure', label: 'Blood Pressure', unit: ' mmHg', metric: vitalSigns.bloodPressure },
  ];

  const hrvMetrics = [
    { key: 'sdnn', label: 'SDNN', unit: ' ms', metric: vitalSigns.sdnn },
    { key: 'rmssd', label: 'RMSSD', unit: ' ms', metric: vitalSigns.rmssd },
    { key: 'sd1', label: 'SD1', unit: ' ms', metric: vitalSigns.sd1 },
    { key: 'sd2', label: 'SD2', unit: ' ms', metric: vitalSigns.sd2 },
    { key: 'meanRri', label: 'Mean RRI', unit: ' ms', metric: vitalSigns.meanRri },
    { key: 'rri', label: 'RRI Data', unit: '', metric: vitalSigns.rri },
    { key: 'prq', label: 'PRQ', unit: '%', metric: vitalSigns.prq },
    { key: 'lfhf', label: 'LF/HF Ratio', unit: '', metric: vitalSigns.lfhf },
  ];

  const stressWellnessMetrics = [
    { key: 'stressLevel', label: 'Stress Level', unit: '', metric: vitalSigns.stressLevel },
    { key: 'stressIndex', label: 'Stress Index', unit: '', metric: vitalSigns.stressIndex },
    { key: 'normalizedStressIndex', label: 'Normalized Stress Index', unit: '', metric: vitalSigns.normalizedStressIndex },
    { key: 'wellnessLevel', label: 'Wellness Level', unit: '', metric: vitalSigns.wellnessLevel },
    { key: 'wellnessIndex', label: 'Wellness Index', unit: '', metric: vitalSigns.wellnessIndex },
  ];

  const ansMetrics = [
    { key: 'pnsIndex', label: 'PNS Index', unit: '', metric: vitalSigns.pnsIndex },
    { key: 'pnsZone', label: 'PNS Zone', unit: '', metric: vitalSigns.pnsZone },
    { key: 'snsIndex', label: 'SNS Index', unit: '', metric: vitalSigns.snsIndex },
    { key: 'snsZone', label: 'SNS Zone', unit: '', metric: vitalSigns.snsZone },
  ];

  const healthMetrics = [
    { key: 'hemoglobin', label: 'Hemoglobin', unit: ' g/dL', metric: vitalSigns.hemoglobin },
    { key: 'hemoglobinA1c', label: 'HbA1c', unit: '%', metric: vitalSigns.hemoglobinA1c },
    { key: 'heartAge', label: 'Heart Age', unit: ' years', metric: vitalSigns.heartAge },
  ];

  const riskMetrics = [
    { key: 'highBloodPressureRisk', label: 'High Blood Pressure Risk', unit: '', metric: vitalSigns.highBloodPressureRisk },
    { key: 'highHemoglobinA1CRisk', label: 'High HbA1c Risk', unit: '', metric: vitalSigns.highHemoglobinA1CRisk },
    { key: 'highFastingGlucoseRisk', label: 'High Fasting Glucose Risk', unit: '', metric: vitalSigns.highFastingGlucoseRisk },
    { key: 'highTotalCholesterolRisk', label: 'High Cholesterol Risk', unit: '', metric: vitalSigns.highTotalCholesterolRisk },
    { key: 'lowHemoglobinRisk', label: 'Low Hemoglobin Risk', unit: '', metric: vitalSigns.lowHemoglobinRisk },
    { key: 'ascvdRisk', label: 'ASCVD Risk', unit: '', metric: vitalSigns.ascvdRisk },
  ];

  const renderMetricSection = (title: string, metrics: any[], showRisk = false) => {
    // Filter out metrics that don't have values
    const availableMetrics = metrics.filter(({ metric }) => {
      // Only show metrics that are enabled and have a valid value
      if (!metric.isEnabled) return false;
      if (metric.value === null || metric.value === undefined) return false;
      
      // For arrays (like RRI data), check if array has content
      if (Array.isArray(metric.value) && metric.value.length === 0) return false;
      
      // For objects (like blood pressure), check if it has required properties
      if (typeof metric.value === 'object' && !Array.isArray(metric.value)) {
        if (!metric.value.systolic || !metric.value.diastolic) return false;
      }
      
      return true;
    });

    // Don't render the section if no metrics are available
    if (availableMetrics.length === 0) return null;

    return (
      <Section>
        <SectionTitle>{title}</SectionTitle>
        <MetricsGrid>
          {availableMetrics.map(({ key, label, unit, metric }) => (
            <MetricCard key={key} isEnabled={metric.isEnabled}>
              <MetricLabel>{label}</MetricLabel>
              <MetricValue isEnabled={metric.isEnabled}>
                {formatValue(metric, unit)}
              </MetricValue>
              {metric.confidenceLevel && (
                <ConfidenceLevel level={getConfidenceLevelText(metric.confidenceLevel)}>
                  Confidence: {getConfidenceLevelText(metric.confidenceLevel)}
                </ConfidenceLevel>
              )}
              {showRisk && metric.isEnabled && metric.value && (
                <RiskLevel risk={metric.value}>
                  {getRiskLevelText(metric.value)}
                </RiskLevel>
              )}
            </MetricCard>
          ))}
        </MetricsGrid>
      </Section>
    );
  };

  return (
    <Overlay onClick={(e) => {
      e.stopPropagation();
      onClose();
    }}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Health Measurement Results</Title>
          <CloseButtonWrapper>
            <CloseButton onClick={onClose} />
          </CloseButtonWrapper>
        </Header>
        
        <Content>
          {renderMetricSection('Primary Vital Signs', primaryMetrics)}
          {renderMetricSection('Heart Rate Variability (HRV)', hrvMetrics)}
          {renderMetricSection('Stress & Wellness Indicators', stressWellnessMetrics)}
          {renderMetricSection('Autonomic Nervous System', ansMetrics)}
          {renderMetricSection('Blood & Health Metrics', healthMetrics)}
          {renderMetricSection('Health Risk Assessments', riskMetrics, true)}
        </Content>
        
        <Footer>
          <ButtonRow>
            <ActionButton onClick={onClose}>
              Close Results
            </ActionButton>
            {smsConfig.enabled && smsConfig.phoneNumbers.length > 0 && (
              <SMSButton onClick={handleManualSMS} disabled={isSending}>
                {isSending ? 'Sending SMS...' : 'Send SMS'}
              </SMSButton>
            )}
          </ButtonRow>
          {lastResult && (
            <SMSStatus success={lastResult.success}>
              {lastResult.message}
            </SMSStatus>
          )}
        </Footer>
      </ModalContent>
    </Overlay>
  );
};

export default ResultsModal;